import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import EmployerLayout from './EmployerLayout';

const PageContainer = styled.div`
  background: #0d1b2a;
  min-height: 100vh;
  padding-top: 64px;
  position: relative;
  overflow: hidden;

  /* Subtle background glows */
  &::before {
    content: '';
    position: absolute;
    top: -10%;
    right: -10%;
    width: 60%;
    height: 60%;
    background: radial-gradient(circle, rgba(45, 189, 150, 0.08) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -10%;
    left: -10%;
    width: 50%;
    height: 50%;
    background: radial-gradient(circle, rgba(45, 189, 150, 0.05) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
`;

const HeaderSection = styled.div`
  padding: 80px 5% 40px;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const AccentLine = styled.div`
  width: 40px;
  height: 2px;
  background: #2dbd96;
`;

const HeaderLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #2dbd96;
`;

const HeaderTitle = styled.h1`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 900;
  color: #ffffff;
  line-height: 1.1;
  margin-bottom: 20px;
  max-width: 800px;
`;

const HeaderSubtitle = styled.p`
  font-size: 1.25rem;
  font-weight: 300;
  color: #8a99a8;
  max-width: 600px;
  line-height: 1.6;
`;

const ContentSection = styled.div`
  padding: 40px 5% 120px;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const MosaicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-auto-rows: 240px;
  gap: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: 220px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: auto;
    gap: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CardTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: rgba(45, 189, 150, 0.15);
  border: 1px solid rgba(45, 189, 150, 0.3);
  border-radius: 100px;
  color: #2dbd96;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ArticleCard = styled(Link)<{ isFeatured?: boolean; isLarge?: boolean }>`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;

  grid-column: ${props => (props.isFeatured ? 'span 4' : props.isLarge ? 'span 3' : 'span 2')};
  grid-row: ${props => (props.isFeatured ? 'span 2' : 'span 1')};

  @media (max-width: 1200px) {
    grid-column: ${props => (props.isFeatured ? 'span 4' : 'span 2')};
    grid-row: ${props => (props.isFeatured ? 'span 2' : 'span 1')};
  }

  @media (max-width: 768px) {
    grid-column: span 2;
    grid-row: auto;
  }

  @media (max-width: 480px) {
    grid-column: span 1;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    border-color: rgba(45, 189, 150, 0.4);
    box-shadow: 0 32px 64px rgba(0, 0, 0, 0.4), 0 0 20px rgba(45, 189, 150, 0.1);
    background: rgba(255, 255, 255, 0.05);

    &::after {
      opacity: 1;
    }
  }

  /* Hover glow effect */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      600px circle at var(--mouse-x) var(--mouse-y),
      rgba(45, 189, 150, 0.06),
      transparent 40%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 60%;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 40%;
    background: linear-gradient(180deg, transparent 0%, #0d1b2a 100%);
    opacity: 0.6;
  }
`;

const ArticleImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);

  ${ArticleCard}:hover & {
    transform: scale(1.1);
  }
`;

const ArticleContent = styled.div<{ isFeatured?: boolean }>`
  padding: ${props => (props.isFeatured ? '32px' : '24px')};
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  z-index: 1;
`;

const ArticleTitle = styled.h3<{ isFeatured?: boolean }>`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: ${props => (props.isFeatured ? 'clamp(1.5rem, 3vw, 2.25rem)' : '1.25rem')};
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 12px;
  line-height: 1.3;
  transition: color 0.3s ease;

  ${ArticleCard}:hover & {
    color: #2dbd96;
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.85rem;
  color: #8a99a8;
  margin-top: auto;
`;

const Author = styled.span`
  color: #ffffff;
  font-weight: 500;
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 100px 20px;
  color: #8a99a8;
  font-size: 1.2rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 100px 20px;
  color: #8a99a8;
`;

const LoadMoreTrigger = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 80px;
`;

const LoadMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 40px;
  background: rgba(45, 189, 150, 0.1);
  border: 1px solid rgba(45, 189, 150, 0.4);
  border-radius: 100px;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #2dbd96;
    color: #0d1b2a;
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(45, 189, 150, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

interface IArticle {
  id: number;
  slug: string;
  title: string;
  image?: string;
  published_at?: string;
  category?: string;
  author?: string;
}

const CATEGORIES = ['Trending', 'Insights', 'Industry News', 'Recruitment', 'Career Advice'];

const Articles = ({ t }) => {
  const [articles, setArticles] = useState<IArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Added mouse light effect logic
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const handleMouseMove = (e: MouseEvent) => {
      const cards = document.getElementsByClassName('mosaic-card');
      for (const card of cards as any) {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        card.style.setProperty('--mouse-x', `${x}px`);
        card.style.setProperty('--mouse-y', `${y}px`);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const fetchArticles = async (pageNum = 1, isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      const response = await fetch(`/api/v1/articles?page=${pageNum}&per_page=20`);
      if (!response.ok) throw new Error('Failed to fetch articles');
      const data = await response.json();

      const newArticles = (data.articles?.data || []).map((art: any, index: number) => ({
        ...art,
        category: art.category || CATEGORIES[(pageNum * index) % CATEGORIES.length],
        author: art.author || 'The Sainoo Team'
      }));

      const total = data.meta?.total || 0;

      setArticles(prev => (pageNum === 1 ? newArticles : [...prev, ...newArticles]));
      setHasMore(pageNum * 20 < total);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError(err.message);
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles(1, true);
  }, []);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    const nextPage = page + 1;
    await fetchArticles(nextPage);
    setPage(nextPage);
    setLoadingMore(false);
  };

  const formatDate = (dateStr?: string) => {
    return dateStr ? new Date(dateStr).toLocaleDateString() : 'Just now';
  };

  return (
    <EmployerLayout>
      <PageContainer>
        <HeaderSection>
          <HeaderTop>
            <AccentLine />
            <HeaderLabel>Editorial insights</HeaderLabel>
          </HeaderTop>
          <HeaderTitle>Sainoo Media</HeaderTitle>
          <HeaderSubtitle>
            Navigating the future of high-performance recruitment and corporate career growth.
          </HeaderSubtitle>
        </HeaderSection>

        <ContentSection>
          {loading && <LoadingState>Preparing mosaic...</LoadingState>}

          {error && <EmptyState>Unable to load the feed. Please try again later.</EmptyState>}

          {!loading && !error && articles.length > 0 && (
            <>
              <MosaicGrid>
                {articles.map((article, index) => {
                  const isFeatured = index === 0;
                  // Alternate larger boxes for a mosaic feel
                  const isLarge = !isFeatured && (index === 4 || index === 9 || index === 14);

                  return (
                    <ArticleCard
                      key={`${article.id}-${index}`}
                      className="mosaic-card"
                      to={`/?article=${article.slug}`}
                      isFeatured={isFeatured}
                      isLarge={isLarge}
                    >
                      <ImageWrapper style={{ height: isFeatured ? '65%' : '55%' }}>
                        <ArticleImage src={article.image || '/images/placeholder-article.jpg'} alt={article.title} />
                      </ImageWrapper>
                      <ArticleContent isFeatured={isFeatured}>
                        <CardTag>{article.category}</CardTag>
                        <ArticleTitle isFeatured={isFeatured}>{article.title}</ArticleTitle>
                        <ArticleMeta>
                          <Author>{article.author}</Author>
                          <Dot />
                          <span>{formatDate(article.published_at)}</span>
                        </ArticleMeta>
                      </ArticleContent>
                    </ArticleCard>
                  );
                })}
              </MosaicGrid>

              {hasMore && (
                <LoadMoreTrigger>
                  <LoadMoreButton onClick={handleLoadMore} disabled={loadingMore}>
                    {loadingMore ? <Spinner /> : 'Explore more stories'}
                  </LoadMoreButton>
                </LoadMoreTrigger>
              )}
            </>
          )}
        </ContentSection>
      </PageContainer>
    </EmployerLayout>
  );
};

export default withTranslation(['translations'])(Articles);
